import React from 'react';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/partials/Breadcrumb';

const RefundPolicyPage: React.FC = () => {
  return (
    <DashboardLayout>
      <div className="px-4 py-5 md:px-10 md:py-8">
        <div className="mx-auto max-w-3xl">
          <Breadcrumb current="Refund & Cancellation Policy" />

          <div className="rounded-md border border-theme-border bg-surface px-4 py-8 shadow sm:px-8 sm:py-10">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-tertiary">
              Digital Ascension Group
            </p>
            <h1 className="mb-2 text-2xl font-bold text-on-surface sm:text-3xl">
              Refund &amp; Cancellation Policy
            </h1>
            <p className="mb-8 text-sm text-on-surface-tertiary">
              Effective date: August 21, 2026
            </p>

            <div className="space-y-8 text-sm leading-relaxed text-on-surface-secondary">
              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  1. Overview
                </h2>
                <p>
                  This Refund and Cancellation Policy (&ldquo;Policy&rdquo;)
                  governs all applicable purchases of products and services
                  specifically offered by Digital Ascension Group through its
                  Private Client division (&ldquo;Company,&rdquo;
                  &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By
                  completing any purchase or enrollment with Company, you
                  acknowledge that you have read, understood, and agreed to the
                  terms of this Policy in full.
                </p>
                <p>
                  This Policy applies to all Company clients, members, and
                  purchasers, regardless of the method of enrollment or payment.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  2. All Sales Are Final
                </h2>
                <p>
                  All purchases made through Company are final and
                  non-refundable. This applies without exception to:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    Monthly subscription fees for the Company Compliance and
                    Trading Membership
                  </li>
                  <li>One-time LLC formation and setup fees</li>
                  <li>One-time trust formation fees</li>
                  <li>
                    Any other product, service, or digital offering provided by
                    Company
                  </li>
                </ul>
                <p>
                  No refunds will be issued based on, including but not limited
                  to, the following:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    A client&apos;s decision to cancel or discontinue service
                  </li>
                  <li>Dissatisfaction with results or outcomes</li>
                  <li>Failure to utilize the services provided</li>
                  <li>
                    A promotional sale or price reduction occurring after the
                    date of purchase
                  </li>
                  <li>A change in personal circumstances</li>
                </ul>
                <p>
                  For the avoidance of doubt: if Company offers a promotional
                  discount or sale after your purchase date, you are not
                  entitled to a refund of the difference or any partial credit.
                  All pricing is fixed at the time of purchase.
                </p>
              </section>

              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-on-surface">
                  3. Monthly Subscription &mdash; Compliance &amp; Trading
                  Membership
                </h2>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    3.1 What the Subscription Includes
                  </h3>
                  <p>
                    The Company monthly subscription is priced at $100.00 per
                    month and includes:
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>Access to LLC compliance management services</li>
                    <li>
                      A compliance-tagged trading account powered by Uphold
                    </li>
                    <li>
                      Annual LLC renewal on behalf of the subscriber, so long as
                      the subscription remains active and in good standing
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    3.2 Subscription Billing and Access
                  </h3>
                  <p>
                    Subscription fees are billed on a recurring monthly basis.
                    Continued payment of the monthly subscription fee is a
                    condition of continued service. Company will renew a
                    subscriber&apos;s LLC annually only while the subscription
                    remains active and all payments are current.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    3.3 Cancellation by Client
                  </h3>
                  <p>
                    Clients may cancel their subscription at any time. Upon
                    cancellation:
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      No refund will be issued for the current billing period,
                      whether full or partial.
                    </li>
                    <li>
                      The client will retain access to subscription benefits
                      through the end of the current paid billing cycle.
                    </li>
                    <li>
                      Company will not renew the client&apos;s LLC following
                      cancellation. Responsibility for LLC maintenance and
                      renewal reverts entirely to the client upon cancellation.
                    </li>
                    <li>
                      No partial-month pro-rated refunds will be issued under
                      any circumstance.
                    </li>
                  </ul>
                  <p>
                    Example: If a client&apos;s billing date is the 1st of the
                    month and they cancel on the 15th, they will retain access
                    through the end of that month. No refund will be issued for
                    the remaining days of the cycle.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    3.4 LLC Lapse Due to Non-Payment or Cancellation
                  </h3>
                  <p>
                    Company bears no responsibility or liability for any LLC
                    that lapses, is dissolved, or falls out of compliance as a
                    result of a client&apos;s cancellation of their subscription
                    or failure to maintain timely payment. It is the
                    client&apos;s sole responsibility to arrange for LLC renewal
                    upon cancellation.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-on-surface">
                  4. One-Time Services
                </h2>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    4.1 LLC Formation
                  </h3>
                  <p>
                    One-time LLC formation fees are non-refundable once the
                    formation process has been initiated. This includes any
                    state filing fees, registered agent fees, or administrative
                    fees paid as part of the formation service. Initiation of
                    the formation process constitutes delivery of services.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    4.2 Trust Formation
                  </h3>
                  <p>
                    Trust formation is offered as a one-time service. All trust
                    formation fees are non-refundable once the drafting or
                    formation process has commenced. Delivery of trust
                    documentation, whether in draft or final form, constitutes
                    delivery of services for purposes of this Policy.
                  </p>
                </div>
              </section>

              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-on-surface">
                  5. Subscription Lapse and Reinstatement
                </h2>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    5.1 Effect of Non-Payment
                  </h3>
                  <p>
                    If a client fails to make a monthly subscription payment,
                    their account will be considered lapsed. During a lapsed
                    period:
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      Access to all subscription benefits, including the
                      compliance-tagged trading account powered by Uphold, will
                      be suspended or terminated.
                    </li>
                    <li>
                      Company will not perform any LLC renewal, compliance
                      management, or other subscription services on behalf of
                      the client.
                    </li>
                    <li>
                      If a client&apos;s annual LLC renewal comes due during a
                      lapsed period, Company will take no action. The LLC may
                      lapse, lose good standing, or be dissolved by the
                      applicable state. Company bears no liability for any
                      consequences arising from an LLC lapsing during a period
                      of non-payment.
                    </li>
                  </ul>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    5.2 Reinstatement Requirements
                  </h3>
                  <p>
                    A lapsed client who wishes to reinstate their subscription
                    must satisfy the following requirements before reinstatement
                    will be considered:
                  </p>
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      Payment of all missed monthly subscription fees for the
                      period of lapse (at $100.00 per month), OR payment of a
                      flat reinstatement fee of $1,200.00 &mdash; whichever
                      amount is lower at the time reinstatement is requested.
                    </li>
                    <li>
                      Payment of the current month&apos;s subscription fee to
                      establish the new billing cycle.
                    </li>
                  </ul>
                  <p>
                    Example: A client who lapses for 8 months would owe $800 in
                    missed payments, which is less than the $1,200 flat
                    reinstatement fee. That client would therefore be required
                    to pay the $800 reinstatement fee plus the current
                    month&apos;s $100 subscription fee before reinstatement is
                    processed. A client who lapses for 12 months would owe
                    $1,200 in missed payments, which exceeds the flat fee, and
                    would therefore owe $1,200 plus the current month&apos;s
                    fee.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    5.3 Reinstatement at Company&apos;s Discretion
                  </h3>
                  <p>
                    Reinstatement is not guaranteed. Company reserves the sole
                    and absolute right to decline reinstatement to any lapsed
                    client for any reason, including but not limited to prior
                    payment history, account conduct, or changes in service
                    availability. Payment of reinstatement fees does not create
                    an obligation on the part of Company to reinstate the
                    account until reinstatement is formally confirmed by the
                    Company.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    5.4 New Billing Cycle Upon Reinstatement
                  </h3>
                  <p>
                    Upon approved reinstatement, the client will be enrolled on
                    a fresh monthly billing cycle beginning on the date
                    reinstatement is confirmed. Prior billing dates are not
                    carried over. All reinstated accounts are treated as new
                    subscriptions for billing purposes.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="font-semibold text-on-surface">
                    5.5 LLC Status Upon Reinstatement
                  </h3>
                  <p>
                    Reinstatement of a subscription does not automatically
                    restore or cure any LLC that lapsed, was dissolved, or fell
                    out of good standing during the period of non-payment. The
                    client is solely responsible for addressing any state-level
                    LLC issues that arose during the lapsed period. Company will
                    resume LLC compliance and renewal services going forward
                    only after reinstatement is confirmed and only for LLCs that
                    are in good standing at the time of reinstatement.
                  </p>
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  6. Termination by Company
                </h2>
                <p>
                  Company reserves the right to terminate or suspend a
                  client&apos;s account and access to services under the
                  following circumstances:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    Non-payment of subscription fees or any outstanding balance
                  </li>
                  <li>
                    Violation of Company&apos;s Terms of Service, use policies,
                    or any applicable laws or regulations
                  </li>
                  <li>Fraudulent, abusive, or otherwise harmful conduct</li>
                </ul>
                <p>
                  In the event that Company terminates a client&apos;s account
                  for any of the reasons outlined above, no refund of any kind
                  will be issued for fees already paid, including any unused
                  portion of the current billing period.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  7. Third-Party Services &mdash; Uphold Trading Platform
                </h2>
                <p>
                  The Company subscription includes access to a
                  compliance-tagged trading account powered by Uphold, a
                  third-party financial platform. Company is not responsible for
                  interruptions, outages, changes in functionality, account
                  restrictions, or any other issues arising from Uphold&apos;s
                  platform that are outside of Company&apos;s control.
                </p>
                <p>
                  No refunds or credits will be issued as a result of, including
                  but not limited to, the following:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Scheduled or unscheduled maintenance by Uphold</li>
                  <li>
                    Platform outages or technical failures on Uphold&apos;s end
                  </li>
                  <li>
                    Changes to Uphold&apos;s terms, policies, or supported
                    features
                  </li>
                  <li>
                    Account restrictions or actions taken by Uphold independent
                    of Company
                  </li>
                </ul>
                <p>
                  Clients are encouraged to review Uphold&apos;s terms of
                  service and privacy policy independently. Company makes no
                  guarantee of uninterrupted access to third-party platforms.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  8. Refund Method
                </h2>
                <p>
                  In the rare and exceptional circumstance where Company, at its
                  sole discretion, approves a refund, the following conditions
                  apply:
                </p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    All approved refunds will be issued exclusively to the
                    original payment method used at the time of purchase.
                  </li>
                  <li>
                    Company will not issue refunds to an alternate credit card,
                    bank account, digital wallet, cryptocurrency address, or any
                    other payment method.
                  </li>
                  <li>
                    Refunds will not be issued in cash or via check unless
                    expressly required by applicable law.
                  </li>
                </ul>
                <p>
                  Any approved refund may take 5&ndash;10 business days to
                  appear, depending on the processing times of your financial
                  institution.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  9. Chargebacks and Payment Disputes
                </h2>
                <p>
                  We encourage you to contact the Company prior to initiating a
                  chargeback or payment dispute with your financial institution.
                  You retain the right to contact your financial institution
                  regarding any charge at any time. However, please note that
                  chargebacks filed for services that were already delivered as
                  described, or without first attempting to resolve the issue
                  with us, may be contested by the Company with supporting
                  documentation, such as proof of delivery, usage records, or
                  correspondence. Company reserves the right to decline future
                  services to clients with a pattern of abusive or fraudulent
                  dispute activity.
                </p>
                <p>
                  If you believe you have been charged in error, please contact
                  us directly at the information provided in Section 11 before
                  initiating any dispute with your payment provider. In our
                  experience, most billing concerns can be resolved more quickly
                  this way than through the formal dispute process.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  10. No Grace Period
                </h2>
                <p>
                  Company does not offer a grace period, free trial, or
                  money-back guarantee on any product or service. All sales are
                  final immediately upon payment. Clients are encouraged to
                  review all product and service descriptions carefully before
                  completing a purchase.
                </p>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  11. Contact Information
                </h2>
                <p>
                  For questions or concerns regarding this Policy, or to inquire
                  about your account, please contact Digital Ascension Group at:
                </p>
                <address className="not-italic space-y-1">
                  <p>5910 North Central Expressway, Suite 1450</p>
                  <p>Dallas, TX 75206</p>
                  <p>support@dag.com</p>
                  <p>307-214-0780</p>
                </address>
              </section>

              <section className="space-y-3">
                <h2 className="text-lg font-semibold text-on-surface">
                  12. Amendments to This Policy
                </h2>
                <p>
                  Company reserves the right to modify or update this Policy at
                  any time. Changes will be effective upon posting of the
                  revised Policy. Continued use of Company&apos;s products or
                  services following any such modification constitutes
                  acceptance of the revised terms.
                </p>
              </section>

              <section className="space-y-2 border-t border-theme-border pt-6">
                <p className="text-xs font-semibold text-on-surface">
                  Legal Disclaimer
                </p>
                <p className="text-xs italic text-on-surface-tertiary">
                  This document is provided for general business purposes and
                  does not constitute legal advice. Company recommends
                  consulting with a licensed attorney to ensure this Policy
                  complies with all applicable federal, state, and local laws,
                  including consumer protection statutes in jurisdictions where
                  clients reside.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RefundPolicyPage;
